import { useNavigate } from 'react-router-dom';
import { Pin, PinOff, Trash2 } from 'lucide-react';
import { useAllNotes, useNotes } from '@/composables/crm/useNotes';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate } from '@/lib/formatters';
import type { Note } from '@/types/table';

function entityLabel(note: Note & { lead?: { id: string; firstName: string; lastName: string }; contact?: { id: string; firstName: string; lastName: string }; company?: { id: string; name: string }; deal?: { id: string; title: string } }) {
  if (note.lead) return { label: `Lead: ${note.lead.firstName} ${note.lead.lastName}`, href: `/leads/${note.lead.id}` };
  if (note.contact) return { label: `Contact: ${note.contact.firstName} ${note.contact.lastName}`, href: `/contacts/${note.contact.id}` };
  if (note.company) return { label: `Company: ${note.company.name}`, href: `/companies/${note.company.id}` };
  if (note.deal) return { label: `Deal: ${note.deal.title}`, href: `/deals/${note.deal.id}` };
  return null;
}

export function NotesPage() {
  const navigate = useNavigate();
  const { notes, isLoading } = useAllNotes();
  const { deleteNote, togglePin } = useNotes();

  if (isLoading) return <LoadingSpinner />;

  const pinned = notes.filter((n) => n.isPinned);
  const unpinned = notes.filter((n) => !n.isPinned);
  const sorted = [...pinned, ...unpinned];

  return (
    <PageContainer title="Notes" description="All notes across leads, contacts, companies, and deals">
      {sorted.length === 0 && (
        <p className="text-muted-foreground text-center py-16">No notes yet. Add notes from any lead, contact, company, or deal page.</p>
      )}
      <div className="space-y-3">
        {sorted.map((note) => {
          const entity = entityLabel(note as Parameters<typeof entityLabel>[0]);
          const entityType = note.leadId ? 'lead' : note.contactId ? 'contact' : note.companyId ? 'company' : note.dealId ? 'deal' : undefined;
          const entityId = note.leadId || note.contactId || note.companyId || note.dealId;

          return (
            <Card key={note.id} className={note.isPinned ? 'border-primary/40 bg-primary/5' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-muted-foreground">{formatDate(note.createdAt)}</span>
                      {entity && (
                        <button
                          onClick={() => navigate(entity.href)}
                          className="text-xs text-primary hover:underline"
                        >
                          {entity.label}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => togglePin.mutate({ id: note.id, entityType, entityId })}
                    >
                      {note.isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => deleteNote.mutate({ id: note.id, entityType, entityId })}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PageContainer>
  );
}
